/**
 * Motor de tiempos y sobrestadía.
 *
 * Reemplaza las columnas AB..AI de la hoja «GUIAS DE DESPACHO» del Excel.
 * Corrige el defecto E6 del análisis: el Excel restaba fracciones de día y
 * producía tiempos negativos (hasta −0,98 d = −23,5 h) cuando el turno cruzaba
 * la medianoche. Aquí se opera sobre instantes completos, así que eso no puede pasar.
 *
 * Ver ANALISIS-PLANILLAS.md §3.6 y PROMPT-MAESTRO §5.1.
 */

import type { MarcasParciales, TiemposGuia } from './tipos';

/** Minutos de estadía en obra sin costo. Celda $AM$4 del Excel = 0,03125 d = 45 min. */
export const MINUTOS_LIBRES_ESTADIA = 45;

/** Tamaño del bloque facturable. Celda $AN$4 = 0,00694 d = 10 min. */
export const MINUTOS_POR_BLOQUE = 10;

/**
 * Umbral de la regla de redondeo CODELCO.
 * El bloque parcial sólo se cobra si la fracción alcanza 0,6 — es decir,
 * se requieren ≥ 6 minutos del bloque siguiente. Es una regla no estándar.
 */
export const UMBRAL_REDONDEO_CODELCO = 0.6;

/** Duración de un ciclo por sobre la cual se marca anomalía (12 h). */
const MINUTOS_CICLO_ABSURDO = 12 * 60;

const MS_POR_MINUTO = 60_000;

/** Diferencia en minutos enteros entre dos instantes. `null` si falta alguno. */
function minutosEntre(a: Date | undefined, b: Date | undefined): number | null {
  if (!a || !b) return null;
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.round((b.getTime() - a.getTime()) / MS_POR_MINUTO);
}

/**
 * Calcula todos los tiempos derivados de una guía.
 *
 * Cuando falta una marca, los tramos afectados quedan en `null` — nunca en 0.
 * El Excel usaba `IFERROR(..., 0)` y convertía el dato ausente en un cero que
 * entraba en los promedios (defecto E7): 2.619 filas afectadas.
 */
export function calcularTiempos(m: MarcasParciales): TiemposGuia {
  const anomalias: string[] = [];

  const trasladoIda = minutosEntre(m.salidaPlanta, m.llegadaObra);
  const espera = minutosEntre(m.llegadaObra, m.inicioDescarga);
  const descarga = minutosEntre(m.inicioDescarga, m.terminoDescarga);
  const salidaObra = minutosEntre(m.terminoDescarga, m.salidaObra);
  const trasladoVuelta = minutosEntre(m.salidaObra, m.llegadaPlanta);

  const tramos = [trasladoIda, espera, descarga, salidaObra, trasladoVuelta];
  const completa = tramos.every((t) => t !== null);

  // Secuencia fuera de orden: una marca posterior es anterior a la previa.
  const etiquetas = [
    'traslado de ida',
    'espera en obra',
    'descarga',
    'salida de obra',
    'traslado de vuelta',
  ] as const;
  tramos.forEach((t, i) => {
    if (t !== null && t < 0) anomalias.push(`Secuencia inválida: ${etiquetas[i]} es negativo.`);
  });

  const trasladoTotal =
    trasladoIda !== null && trasladoVuelta !== null ? trasladoIda + trasladoVuelta : null;

  // Estadía en obra = espera + descarga + salida de obra   (columna AF)
  const estadiaObra =
    espera !== null && descarga !== null && salidaObra !== null
      ? espera + descarga + salidaObra
      : null;

  // Sobre estadía = lo que excede los 45 min libres          (columna AG)
  const sobreEstadia = estadiaObra === null ? null : Math.max(0, estadiaObra - MINUTOS_LIBRES_ESTADIA);

  // Factor en bloques de 10 min, redondeado a 1 decimal      (columna AH)
  const factor10Min =
    sobreEstadia === null ? null : Math.round((sobreEstadia / MINUTOS_POR_BLOQUE) * 10) / 10;

  // Regla CODELCO                                            (columna AI)
  const bloquesCodelco = factor10Min === null ? null : aplicarRedondeoCodelco(factor10Min);

  const cicloTotal =
    trasladoTotal !== null && estadiaObra !== null ? trasladoTotal + estadiaObra : null;
  if (cicloTotal !== null && cicloTotal > MINUTOS_CICLO_ABSURDO) {
    anomalias.push(
      `Ciclo de ${(cicloTotal / 60).toFixed(1)} h: revisar las horas, puede faltar un cambio de día.`,
    );
  }

  return {
    trasladoIda,
    trasladoVuelta,
    trasladoTotal,
    espera,
    descarga,
    salidaObra,
    estadiaObra,
    sobreEstadia,
    factor10Min,
    bloquesCodelco,
    completa,
    anomalias,
  };
}

/**
 * Regla de redondeo CODELCO sobre el factor de bloques de 10 minutos.
 * `INT(factor) + IF((factor − INT(factor)) >= 0,6 ; 1 ; 0)`
 *
 * Ejemplos reales del EP 58:
 *   28,7 → 29  (frac 0,7 ≥ 0,6 sube)
 *   20,5 → 20  (frac 0,5 < 0,6 no sube)
 */
export function aplicarRedondeoCodelco(factor: number): number {
  const entero = Math.trunc(factor);
  const fraccion = factor - entero;
  // Tolerancia por representación binaria: 0.6 puede llegar como 0.5999999999999996
  return entero + (fraccion >= UMBRAL_REDONDEO_CODELCO - 1e-9 ? 1 : 0);
}

/**
 * ¿La estadía está por entrar en cobro? Alimenta el semáforo del cronómetro
 * en el formulario de captura.
 *   VERDE    → holgado
 *   AMARILLO → faltan ≤ 10 min para los 45
 *   ROJO     → ya se pasó, está generando cobro
 */
export function nivelEstadia(minutosEstadia: number): 'VERDE' | 'AMARILLO' | 'ROJO' {
  if (minutosEstadia >= MINUTOS_LIBRES_ESTADIA) return 'ROJO';
  if (minutosEstadia >= MINUTOS_LIBRES_ESTADIA - MINUTOS_POR_BLOQUE) return 'AMARILLO';
  return 'VERDE';
}

/**
 * Reconstruye instantes a partir de una fecha operativa y horas sueltas,
 * asumiendo que cada marca posterior no puede ser anterior a la previa:
 * si lo es, cruzó la medianoche y se le suma un día.
 *
 * Es la corrección del defecto E6 y la usa el importador del Excel histórico.
 *
 * @param fechaBase  Día operativo (yyyy-MM-dd) en horario local.
 * @param horas      Fracciones de día del Excel (0..1) u `null`, en orden de ciclo.
 */
export function reconstruirInstantes(
  fechaBase: Date,
  horas: (number | null)[],
): { instantes: (Date | null)[]; cruces: number } {
  const instantes: (Date | null)[] = [];
  let cruces = 0;
  let diasAcumulados = 0;
  let anterior: number | null = null;

  for (const h of horas) {
    if (h === null || Number.isNaN(h)) {
      instantes.push(null);
      continue;
    }
    if (anterior !== null && h < anterior) {
      diasAcumulados += 1;
      cruces += 1;
    }
    const d = new Date(fechaBase);
    d.setDate(d.getDate() + diasAcumulados);
    // La fracción de día del Excel a milisegundos desde medianoche local.
    d.setHours(0, 0, 0, 0);
    d.setTime(d.getTime() + Math.round(h * 86_400_000));
    instantes.push(d);
    anterior = h;
  }

  return { instantes, cruces };
}

/** Formatea minutos como `H:MM` o `MM min`. Para la UI. */
export function formatearMinutos(min: number | null): string {
  if (min === null) return '—';
  const signo = min < 0 ? '-' : '';
  const abs = Math.abs(min);
  if (abs < 60) return `${signo}${abs} min`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${signo}${h}:${String(m).padStart(2, '0')} h`;
}
